import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useUnmount } from 'react-use';
import videojsWrapper from 'utils/videojsWrapper';
import videojs from 'video.js';
import { Camera } from 'types/camera';
import { useCamMetrics } from 'components/CamPlayerV2/useCamMetrics';
import {
  RewindStitcherVideoJsPlayer,
  RewindVideo,
} from './plugins/rewindStitcher/src/rewindStitcher.types';
import styles from './RewindCamPlayer.module.scss';
import { rewindStitcher } from './plugins/rewindStitcher/src/plugin';

export interface RewindCamPlayerProps {
  rewinds: RewindVideo[];
  beginning: Date | null;
  ending: Date | null;
  onPlay: (playing: boolean) => void;
  playbackSpeed?: number;
  camera: Camera;
  setCurrentTimestamp: (current: Date) => void;
  setIsBuffering: (buffering: boolean) => void;
  onRewindListEnded?: () => void;
  onPlayerError: (code: number | undefined) => void;
  setIsVideoPlayerLoaded: (isLoaded: boolean) => void;
  setVideoLoaded?: (videoLoaded: boolean) => void;
  customPlayerOptions?: any;
  onPlayedDurationUpdate?: (playedMs: number, resetPlayedMs: () => void) => void;
}

export interface RewindCamPlayerHandle {
  play: () => void;
  playAtTimestamp: (timestamp: Date, forcePlay?: boolean) => void;
  pause: () => void;
  toggleNativeFullscreen: () => void;
  requestNativePictureInPicture: () => void;
  exitNativePictureInPicture: () => void;
  getPlayer: () => RewindStitcherVideoJsPlayer | null;
}

export const RewindCamPlayer = forwardRef<RewindCamPlayerHandle, RewindCamPlayerProps>(
  (
    {
      rewinds,
      beginning,
      ending,
      onPlay,
      camera,
      playbackSpeed = 1,
      setCurrentTimestamp,
      setIsBuffering,
      onRewindListEnded,
      onPlayerError,
      setIsVideoPlayerLoaded,
      setVideoLoaded,
      customPlayerOptions,
      onPlayedDurationUpdate,
    },
    playerHandleRef,
  ) => {
    const videoRef = useRef<HTMLDivElement | null>(null);
    const [player, setPlayer] = useState<RewindStitcherVideoJsPlayer | null>(null);
    const playedMs = useRef<number>(0);
    const previousTimestamp = useRef<Date | null>(null);

    const resetPlayer = useCallback(() => {
      // Setting the source to the current source removes the error state and resets the player
      player?.src(player.currentSource());
    }, [player]);

    useImperativeHandle(
      playerHandleRef,
      () => ({
        play() {
          player?.play();
          onPlayedDurationUpdate?.(playedMs.current, () => {
            playedMs.current = 0;
          });
        },
        playAtTimestamp(timestamp: Date, forcePlay: boolean = false) {
          if (player?.error()) {
            console.log({ playerError: player?.error() });
            resetPlayer();
          }
          player?.rewindStitcherControls.playAtTimestamp(new Date(timestamp), forcePlay);
          setCurrentTimestamp(new Date(timestamp));
        },
        pause() {
          player?.pause();
        },
        toggleNativeFullscreen() {
          if (!player?.isFullscreen()) {
            player?.requestFullscreen();
          } else {
            player?.exitFullscreen();
          }
        },
        async requestNativePictureInPicture() {
          const videoElement = videoRef.current?.querySelector('video') as HTMLVideoElement;
          if (
            videoElement &&
            window.document.pictureInPictureEnabled &&
            !videoElement.disablePictureInPicture
          ) {
            try {
              await videoElement.requestPictureInPicture();
            } catch (error) {
              console.error('Error requesting picture-in-picture:', error);
            }
          }
        },
        getPlayer() {
          return player;
        },
        exitNativePictureInPicture() {
          if (window.document.pictureInPictureElement) {
            window.document.exitPictureInPicture?.();
          }
        },
      }),
      [player, resetPlayer, setCurrentTimestamp, onPlayedDurationUpdate],
    );

    const playerOptions = useMemo(
      () => ({
        aspectRatio: '16:9',
        autoplay: false,
        controls: false,
        disablePictureInPicture: false,
        inactivityTimeout: 4000,
        muted: true,
        playsinline: true,
        plugins: {
          hotkeys: {
            enableMute: false,
            enableVolumeScroll: false,
          },
        },
        preload: 'metadata', // 'auto' is not supported in Safari iOS as it causes infinite buffering until pressing play
        ...customPlayerOptions,
      }),
      [customPlayerOptions],
    );

    const timeupdate = useCallback(() => {
      if (
        player?.scrubbing() ||
        player?.seeking() ||
        player?.bufferedPercent() === 0 ||
        player?.paused()
      ) {
        return;
      }

      setIsBuffering(false);

      const current = player?.rewindStitcherControls.getCurrentTimetstamp();

      if (current) {
        setCurrentTimestamp(current);
        if (previousTimestamp.current && previousTimestamp.current.getTime() < current.getTime()) {
          playedMs.current += Math.min(
            1000,
            current.getTime() - previousTimestamp.current.getTime(),
          );
          onPlayedDurationUpdate?.(playedMs.current, () => {
            playedMs.current = 0;
          });
        }
        previousTimestamp.current = current;
      }
    }, [player, setCurrentTimestamp, setIsBuffering, onPlayedDurationUpdate]);

    useEffect(() => {
      player?.playbackRate(playbackSpeed || 1);
    }, [player, playbackSpeed]);

    const handleError = useCallback(() => {
      if (player) {
        const error = player?.error();
        onPlayerError(error?.code);
      }
    }, [player, onPlayerError]);

    const onPlayCallback = useCallback(() => onPlay(true), [onPlay]);
    const onPauseCallback = useCallback(() => onPlay(false), [onPlay]);
    const onVideoLoaded = useCallback(() => {
      setVideoLoaded?.(true);
      player?.playbackRate(playbackSpeed || 1);
    }, [setVideoLoaded, player, playbackSpeed]);

    const handleContextMenu = useCallback((event: React.MouseEvent<HTMLVideoElement> | Event) => {
      // Disable the context menu so the user can't enable the native HTML5 video controls
      event.preventDefault();
      return false;
    }, []);

    useEffect(() => {
      if (player) {
        player.on('contextmenu', handleContextMenu);
        player.on('loadeddata', onVideoLoaded);
        player.on('timeupdate', timeupdate);
        player.on('play', onPlayCallback);
        player.on('playing', onPlayCallback);
        player.on('pause', onPauseCallback);
        if (onRewindListEnded) {
          player.on('rewindStitcher:rewindListEnded', onRewindListEnded);
        }
        player.on('error', handleError);

        return () => {
          player.off('loadeddata', onVideoLoaded);
          player.off('timeupdate', timeupdate);
          player.off('playing', onPlayCallback);
          player.off('play', onPlayCallback);
          player.off('pause', onPauseCallback);
          player.off('contextmenu', handleContextMenu);
          if (onRewindListEnded) {
            player.off('rewindStitcher:rewindListEnded', onRewindListEnded);
          }
          player.off('error', handleError);
        };
      }

      return undefined;
    }, [
      player,
      timeupdate,
      onPlayCallback,
      onPauseCallback,
      onRewindListEnded,
      handleError,
      onVideoLoaded,
      handleContextMenu,
    ]);

    useCamMetrics({
      camPlayer: player,
      camera,
      isRewindClipsPlayer: true,
    });

    useEffect(() => {
      // Make sure Video.js player is only initialized once
      if (!player) {
        // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
        const videoElement = document.createElement('video-js');
        videoElement.classList.add('vjs-sl-cam-player');
        videoRef.current?.appendChild(videoElement);

        if (!videojs.getPlugin('rewindStitcher')) {
          videojs.registerPlugin('rewindStitcher', rewindStitcher);
        }

        const videoJsPlayer = videojsWrapper(
          videoElement,
          playerOptions,
        ) as unknown as RewindStitcherVideoJsPlayer;

        const stitcherOptions = {
          rewinds,
          start: beginning,
          end: ending,
        };

        videoJsPlayer.rewindStitcher(stitcherOptions);
        setPlayer(videoJsPlayer);
        setIsVideoPlayerLoaded(true);
        onPlay(!videoJsPlayer.paused());
      } else {
        // ensure rewind list is up to date
        player.rewindStitcherControls.setRewindList(rewinds);
        player.rewindStitcherControls.setStart(beginning);
        player.rewindStitcherControls.setEnd(ending);
      }

      playedMs.current = 0;
    }, [
      rewinds,
      beginning,
      ending,
      player,
      playerOptions,
      timeupdate,
      setIsBuffering,
      setIsVideoPlayerLoaded,
      onPlay,
    ]);

    useUnmount(() => {
      if (player && !player.isDisposed()) {
        try {
          player.dispose();
          setPlayer(null);
        } catch (error) {
          console?.error(error);
        }
      }
    });

    return (
      <div
        className={styles.videoPlayer}
        ref={videoRef}
        data-testid="kbyg-rewinds-rewind-cam-player"
      />
    );
  },
);

RewindCamPlayer.displayName = 'RewindCamPlayer';

export default RewindCamPlayer;
